import React, { useMemo, useState } from "react";

import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { BinDeleteIcon } from "assets/material-custom-icons";
import { FileViewer } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { MonitoringDataFiles, UiMeasureDataFiles } from "store/types";
import { formatDate } from "utils";
import { isPreviewableFileName } from "utils/files";

import { FileType } from "../../../constants";

interface Props {
  files: UiMeasureDataFiles;
  parameter: string;
  investor: string;
  date: string;
  onDeleteFile: (localId: string) => void;
}

export const FilesTable: React.FC<Props> = ({ files, parameter, investor, date, onDeleteFile }) => {
  const columns = ["Name", "Parameter", "Investor", "Date", ""];
  const [pdfForViewer, setPdfForViewer] = useState<{
    file_url: string;
    type: FileType;
    name: string;
    isBlobUrl?: boolean;
  } | null>(null);

  const mapFiles = (files: UiMeasureDataFiles | null) => {
    const localFiles = files?.localFiles
      ? Object.entries(files.localFiles).map(([localId, file]) => ({
          localId,
          fileName: file.name,
          parameter: parameter,
          investor: investor,
          date: date,
          file: file
        }))
      : [];
    const dbFiles = files?.dbFiles
      ? Object.entries(files.dbFiles).map(([localId, file]) => ({
          localId,
          fileName: file.name,
          investor: investor,
          parameter: parameter,
          date: date,
          file: file
        }))
      : [];
    return [...localFiles, ...dbFiles];
  };

  const filesData = useMemo(() => mapFiles(files), [files, parameter, investor, date]);

  const filePreviewHandler = (file: File | MonitoringDataFiles) => {
    const isLocalFile = file instanceof File;

    if (isLocalFile) {
      const blobUrl = URL.createObjectURL(file);
      const fileType: FileType = FileType.PDF;

      setPdfForViewer({
        file_url: blobUrl,
        type: fileType,
        name: file.name,
        isBlobUrl: true
      });
    } else {
      const dbFile = file as MonitoringDataFiles;
      setPdfForViewer({
        file_url: dbFile.file_url,
        type: FileType.PDF,
        name: dbFile.name,
        isBlobUrl: false
      });
    }
  };

  return (
    Boolean(filesData.length) && (
      <>
        <TableContainer
          sx={{
            borderRadius: "8px",
            boxShadow: "none",
            border: "0.5px solid #dae0e2",
            overflowX: "visible"
          }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: "rgba(246, 248, 252, 1)" }}>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell sx={cellCommonStyles} align={!index ? "left" : "center"} key={column}>
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filesData.map((item) => (
                <TableRowItem
                  item={item}
                  file={item}
                  onDeleteFile={onDeleteFile}
                  key={item.localId}
                  filePreviewHandler={isPreviewableFileName(item.fileName) ? filePreviewHandler : undefined}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {pdfForViewer?.file_url && (
          <FileViewer
            file={pdfForViewer}
            isOpen={!!pdfForViewer}
            onClose={() => {
              if (pdfForViewer?.isBlobUrl && pdfForViewer?.file_url) {
                URL.revokeObjectURL(pdfForViewer.file_url);
              }
              setPdfForViewer(null);
            }}
          />
        )}
      </>
    )
  );
};

const cellCommonStyles: TableCellProps["sx"] = {
  backgroundColor: "#edf1f9",
  color: "#0a3142",
  fontSize: "15px",
  fontWeight: "normal",
  border: "none",
  whiteSpace: "nowrap",
  padding: "9px 10px !important"
};

const TableRowItem = ({
  item,
  file,
  onDeleteFile,
  filePreviewHandler
}: {
  item: {
    localId: string;
    fileName: string;
    investor: string;
    parameter: string;
    date: string;
    file: File | MonitoringDataFiles;
  };
  file: {
    localId: string;
    fileName: string;
    file: File | MonitoringDataFiles;
  };
  filePreviewHandler?: (file: File | MonitoringDataFiles) => void;
  onDeleteFile: (localId: string) => void;
}) => {
  return (
    <TableRow key={file.localId}>
      <TableCell sx={{ maxWidth: "100px" }}>
        <MaterialTooltip title={file.fileName}>
          <Typography
            onClick={filePreviewHandler ? () => filePreviewHandler(file.file) : undefined}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              cursor: filePreviewHandler ? "pointer" : "default",
              color: filePreviewHandler ? "#26a6ae" : "#0a3142"
            }}
          >
            {file.fileName}
          </Typography>
        </MaterialTooltip>
      </TableCell>
      <TableCell sx={{ maxWidth: "100px" }}>
        <MaterialTooltip title={item.parameter}>
          <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {item.parameter}
          </Typography>
        </MaterialTooltip>
      </TableCell>
      <TableCell sx={{ maxWidth: "100px" }}>
        <MaterialTooltip title={item.investor}>
          <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {item.investor || "Total"}
          </Typography>
        </MaterialTooltip>
      </TableCell>
      <TableCell sx={{ maxWidth: "100px" }}>
        <MaterialTooltip title={formatDate({ date: item.date, format: "DD/MM/YYYY" })}>
          <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {formatDate({ date: item.date, format: "DD/MM/YYYY" })}
          </Typography>
        </MaterialTooltip>
      </TableCell>
      <TableCell>
        <IconButton onClick={() => onDeleteFile(file.localId)}>
          <BinDeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};
